import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../ds/index'
import { post } from '../../lib/api'
import { Link, useSearchParams } from 'react-router-dom'
import { PageHeader, Panel, EmptyState, Select, SegmentedControl } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { money, dateShort, timeShort } from '../../lib/format'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { LeadsTabs } from './LeadsTabs'
import { ClaimButton, isWeekly, tierOf, unbilled, weeklyCost } from './weekly'
import { SortSelect, useSortParam, type LeadSort } from '../../components/SortSelect'

export type Lead = {
  id: string; billed_at: string | null; sent_at: string; grading: boolean; grade_due_at: string | null; client_seen_at: string | null
  list_cents: number | null; prepaid_credit_cents: number; prepaid_cash_cents: number; overage_cents: number; billed_cents: number | null
  outcome: 'contacted' | 'not_yet' | null; outcome_at: string | null
  dispute_status: 'open' | 'approved' | 'denied' | null; dispute_reason: string | null; disputed_at: string | null; dispute_note: string | null
  refunded_at: string | null; dispute_category: string | null; has_dispute_image: boolean
  tier_name: string | null; tier_key: string | null; tier_reason: string | null
  job_type_name?: string | null; lane_name?: string | null; lane_urgent?: boolean
  summary: string | null; urgency: 'today' | 'this_week' | 'flexible' | 'unknown' | null
  area_text: string | null; post_url: string | null; category_name: string | null; area_name: string | null; group_name: string | null
  rep_first_name: string | null; responded_at: string | null; reply_note: string | null; details: string | null
  has_image: boolean; dispute_until: string | null; can_dispute: boolean
  conversation: { id: string; has_image: boolean; note: string | null; created_at: string }[]
  post_text?: string | null; post_text_cut?: boolean | null
  // Weekly billing (terms v1.3)
  billing_cycle?: 'monthly' | 'weekly'; claimed_at?: string | null; claim_source?: string | null; can_claim?: boolean
  locks_at?: string | null; current_tier_name?: string | null; current_tier_key?: string | null; current_full_cents?: number | null
  tab_discount_bps?: number | null; statement_id?: string | null
  person_name?: string | null; profile_url?: string | null; person_phone?: string | null; person_email?: string | null; person_lives_in?: string | null
  phone_removed?: 'registry' | 'own' | null; phone_notice?: string | null; posted_at_text?: string | null
}

export const TIMING: Record<string, string> = { today: 'Needs it today', this_week: 'Within the week', flexible: 'Planning ahead' }

export const monthName = (m: string) => (m === 'all' ? 'all time' : new Date(`${m}-15T12:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }))

/** The tier as a badge: A+ in amber, A in olive, B tinted, C plain. Always labelled, never colour alone. */
export function TierBadge({ name, tierKey, large }: { name: string | null; tierKey: string | null; large?: boolean }) {
  if (!name) return null
  return <span className={`tier-badge t-${tierKey || 'x'}${large ? ' is-lg' : ''}`} aria-label={`Tier ${name}`}>{name}</span>
}

type State = 'new' | 'grading' | 'refunded' | 'disputed' | 'declined' | 'contacted' | 'not_yet' | 'open' | 'to_claim' | 'claimed'
export function leadState(lead: Lead): State {
  if (!lead.client_seen_at) return 'new'
  if (lead.grading) return 'grading'
  if (lead.refunded_at || (lead.dispute_status === 'approved' && !lead.claimed_at)) return 'refunded'
  if (lead.dispute_status === 'open') return 'disputed'
  if (isWeekly(lead) && !lead.billed_at) return lead.claimed_at ? 'claimed' : 'to_claim'
  if (lead.dispute_status === 'denied') return 'declined'
  if (lead.outcome === 'contacted') return 'contacted'
  if (lead.outcome === 'not_yet') return 'not_yet'
  return 'open'
}
const STATE_PILL: Record<State, [string, string]> = {
  grading: ['Being graded', 'is-grading'], refunded: ['Refunded', 'is-good'], disputed: ['Disputed', 'is-warn'],
  declined: ['Dispute declined', ''], contacted: ['Reached out', 'is-good'], not_yet: ['Not yet', ''], new: ['New', 'is-grading'], open: ['Opened', ''],
  to_claim: ['To claim', 'is-warn'], claimed: ['Claimed', 'is-good'],
}

/** One word for where a lead stands, the way the client would say it. */
export function LeadStatus({ lead }: { lead: Lead }) {
  const [label, cls] = STATE_PILL[leadState(lead)]
  return <span className={`pill ${cls}`.trim()}>{label}</span>
}

/** How a lead was paid for, in a few words. */
export function paidHow(lead: Lead) {
  if (lead.grading) return 'Not charged yet'
  const w = isWeekly(lead) ? weeklyCost(lead) : null
  if (w) return w.label
  if (isWeekly(lead) && lead.billed_at) {
    if (lead.prepaid_credit_cents > 0 && lead.overage_cents > 0) return 'Part tab, part card'
    return lead.overage_cents > 0 ? 'Charged to your card' : 'From your tab'
  }
  if (lead.refunded_at) return 'Refunded, no charge'
  if (lead.prepaid_credit_cents > 0 && lead.overage_cents > 0) return 'Part credit, part bill'
  if (lead.overage_cents > 0) return 'On your bill'
  if (lead.prepaid_credit_cents > 0) return 'From your credit'
  return 'On your bill'
}

export const leadCost = (lead: Lead) => (lead.refunded_at || unbilled(lead) ? 0 : lead.billed_cents || 0)

/**
 * "Did they reach out?" in one tap, wherever a lead is shown. It is the answer
 * that tells the company which leads work, so it should never take three taps.
 */
export function QuickAnswer({ lead, compact }: { lead: Lead; compact?: boolean }) {
  const toast = useToast()
  const [outcome, setOutcome] = useState(lead.outcome)
  if (lead.grading || lead.refunded_at) return null
  const answer = async (o: 'contacted' | 'not_yet') => {
    const before = outcome
    setOutcome(o)
    try { await post(`/api/client/leads/${encodeURIComponent(lead.id)}/outcome`, { outcome: o }) }
    catch (e) { setOutcome(before); toast((e as Error).message, 'neg') }
  }
  return (
    <div className="quick-answer" role="group" aria-label="Did they reach out?">
      <span>{compact ? 'Did they call?' : 'Did they reach out to you?'}</span>
      <button type="button" className="qa-btn" aria-pressed={outcome === 'contacted'} onClick={() => answer('contacted')}>Yes</button>
      <button type="button" className="qa-btn" aria-pressed={outcome === 'not_yet'} onClick={() => answer('not_yet')}>Not yet</button>
    </div>
  )
}

/** A lead as a tile: the reply screenshot on top, then what they need, the tier, where it stands, what it cost, and the one-tap answer. */
export function LeadTile({ lead, onChange }: { lead: Lead; onChange?: () => void }) {
  const tier = tierOf(lead)
  const w = isWeekly(lead) ? weeklyCost(lead) : null
  const where = [lead.person_name, lead.area_name || lead.area_text, lead.group_name].filter(Boolean).join(' · ')
  return (
    <article className="lead-tile">
      <Link className="lead-tile-link" to={`/portal/leads/${lead.id}`} aria-label={`Open lead: ${lead.summary || 'a request'}`}>
        <div className="lead-tile-shot">
          {lead.has_image ? <img src={`/api/client/leads/${lead.id}/image`} alt="" loading="lazy" /> : null}
        </div>
        <div className="lead-tile-body">
          <div className="lead-tile-top">
            <TierBadge name={tier.name} tierKey={tier.key} />
            <LeadStatus lead={lead} />
          </div>
          <div className="lead-tile-title">{lead.summary || 'Asked for a recommendation'}</div>
          <div className="lead-tile-meta">
            {where && <span>{where}</span>}
            {lead.urgency && TIMING[lead.urgency] && <span>{TIMING[lead.urgency]}</span>}
            {lead.conversation.length > 0 && <span>{lead.conversation.length + 1} messages</span>}
          </div>
          <div className="lead-tile-foot">
            <span className="muted">{dateShort(lead.sent_at)}, {timeShort(lead.sent_at)}</span>
            {w ? <span><strong className="num">{money(w.amount)}</strong> <span className="muted">{w.label}</span></span>
              : <span><strong className="num">{lead.grading ? 'Not charged yet' : money(leadCost(lead))}</strong> <span className="muted">{lead.grading ? '' : paidHow(lead)}</span></span>}
          </div>
        </div>
      </Link>
      <div className="spread wrap-gap" style={{ padding: '0 var(--s-5) var(--s-5)', alignItems: 'center' }}>
        <QuickAnswer lead={lead} compact />
        {onChange && <ClaimButton lead={lead} onDone={onChange} small />}
      </div>
    </article>
  )
}

/** What prepaying saved on one lead: its normal price less what it cost. */
const savedOn = (lead: Lead) => (unbilled(lead) || lead.refunded_at || lead.list_cents == null ? 0 : Math.max(0, lead.list_cents - (lead.billed_cents || 0)))

/**
 * Every lead as one row: when, what, tier, where it stands, the normal price,
 * what it actually cost, how it was paid, and what prepaying saved. Totals at
 * the foot. On a phone the middle columns fold away and the tier sits in the row.
 */
export function LeadTable({ leads, onChange }: { leads: Lead[]; onChange?: () => void }) {
  const go = useNavigate()
  const list = leads.reduce((t, l) => t + (unbilled(l) || l.refunded_at ? 0 : l.list_cents ?? l.billed_cents ?? 0), 0)
  const paid = leads.reduce((t, l) => t + leadCost(l), 0)
  const saved = leads.reduce((t, l) => t + savedOn(l), 0)
  return (
    <div className="table-wrap">
      <table className="table lead-table">
        <thead>
          <tr>
            <th className="lt-date">Date</th><th>Lead</th><th className="lt-wide">Tier</th><th className="lt-xl">Status</th>
            <th className="cell-right lt-wide">Normal price</th><th className="cell-right">You paid</th>
            <th className="cell-right lt-xl">Saved</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => {
            const where = [l.person_name, l.area_name || l.area_text, l.category_name].filter(Boolean).join(' · ')
            const open = () => go(`/portal/leads/${l.id}`)
            const tier = tierOf(l)
            const w = isWeekly(l) ? weeklyCost(l) : null
            return (
              <tr key={l.id} className={`lead-row-link${l.client_seen_at ? '' : ' is-new'}`} tabIndex={0} onClick={open}
                  onKeyDown={(e) => { if (e.key === 'Enter') open() }} aria-label={`Open lead: ${l.summary || 'a request'}`}>
                <td className="lt-date"><span>{dateShort(l.sent_at)}</span><span className="muted">{timeShort(l.sent_at)}</span></td>
                <td className="lt-lead">
                  <span className="lt-phone"><TierBadge name={tier.name} tierKey={tier.key} /></span>
                  <span className="lt-title">{l.summary || 'Asked for a recommendation'}</span>
                  {where && <span className="muted">{where}</span>}
                  <span className="muted lt-phone-date">{dateShort(l.sent_at)}, {timeShort(l.sent_at)}</span>
                </td>
                <td className="lt-wide"><TierBadge name={tier.name} tierKey={tier.key} /></td>
                <td className="lt-xl"><LeadStatus lead={l} /></td>
                <td className="cell-num-muted lt-wide">{l.grading ? '' : w ? money(w.amount) : money(l.list_cents ?? l.billed_cents)}</td>
                <td className="cell-right lt-paid">
                  {w && l.can_claim && onChange ? <ClaimButton lead={l} onDone={onChange} small /> : <strong className="num">{l.grading || w ? 'Not yet' : money(leadCost(l))}</strong>}
                  <span className="muted lt-small">{paidHow(l)}</span>
                </td>
                <td className="cell-num-muted lt-xl">{savedOn(l) ? money(savedOn(l)) : ''}</td>
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr>
            <td className="lt-date" /><td><strong>{leads.length} lead{leads.length === 1 ? '' : 's'}</strong></td>
            <td className="lt-wide" /><td className="lt-xl" />
            <td className="cell-num-muted lt-wide">{money(list)}</td>
            <td className="cell-num">{money(paid)}</td>
            <td className="cell-num-muted lt-xl">{saved ? money(saved) : ''}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

type View = 'table' | 'tiles'
const SORTS: LeadSort[] = ['newest', 'oldest', 'az', 'za']
const readView = (): View => { try { return localStorage.getItem('hp-leads-view') === 'tiles' ? 'tiles' : 'table' } catch { return 'table' } }

type LeadsPage = { month: string; months: { month: string; leads: number }[]; leads: Lead[] }
const FILTERS: { key: 'all' | State | 'tier'; label: string }[] = [
  { key: 'all', label: 'All' }, { key: 'grading', label: 'Being graded' }, { key: 'new', label: 'New' }, { key: 'contacted', label: 'Reached out' }, { key: 'disputed', label: 'Disputed' },
]
const WEEKLY_FILTERS: { key: 'all' | State | 'tier'; label: string }[] = [
  { key: 'all', label: 'All' }, { key: 'to_claim', label: 'To claim' }, { key: 'claimed', label: 'Claimed' }, { key: 'new', label: 'New' }, { key: 'contacted', label: 'Reached out' }, { key: 'disputed', label: 'Disputed' },
]

export default function ClientLeads() {
  const [params, setParams] = useSearchParams()
  const month = params.get('month') || ''
  const [sort, setSort] = useSortParam(SORTS, 'newest')
  const { data, error, reload } = useApi<LeadsPage>(`/api/client/leads?sort=${sort}${month ? `&month=${encodeURIComponent(month)}` : ''}`)
  const [show, setShow] = useState<string>('all')
  const [tier, setTier] = useState<string>('all')
  const [view, setViewState] = useState<View>(readView)
  const setView = (v: View) => { setViewState(v); try { localStorage.setItem('hp-leads-view', v) } catch { /* private window */ } }

  const listed = data ? (data.month === 'all' || data.months.some((m) => m.month === data.month) ? data.months : [{ month: data.month, leads: 0 }, ...data.months]) : []
  const months = [{ month: 'all', leads: listed.reduce((t, m) => t + m.leads, 0) }, ...listed]
  const tiers = useMemo(() => [...new Map((data?.leads || []).map(tierOf).filter((t) => t.key).map((t) => [t.key!, t.name!])).entries()], [data])
  const shown = (data?.leads || []).filter((l) => (show === 'all' || leadState(l) === show) && (tier === 'all' || tierOf(l).key === tier))
  const weeklyOrg = (data?.leads || []).some(isWeekly)
  const total = data ? data.leads.reduce((s, l) => s + leadCost(l), 0) : 0

  return (
    <>
      <PageHeader title="Leads" subtitle="Every lead sent to you. Open one to see the whole conversation, mark whether they reached out, or dispute it."
        actions={<a className="btn btn-secondary" href="/api/client/leads.csv">Download spreadsheet</a>} />
      <LeadsTabs />
      {error && <ErrorBlock error={error} />}
      {!data ? (!error && <LoadingBlock />) : (
        <>
          <div className="spread wrap-gap" style={{ marginBottom: 'var(--s-5)', alignItems: 'center' }}>
            <Select aria-label="Month" value={data.month} onChange={(e) => setParams((p) => { const n = new URLSearchParams(p); n.set('month', e.target.value); return n }, { replace: true })}>
              {months.map((m) => <option key={m.month} value={m.month}>{m.month === 'all' ? 'All time' : monthName(m.month)}{m.leads ? ` · ${m.leads} lead${m.leads === 1 ? '' : 's'}` : ''}</option>)}
            </Select>
            {data.leads.length > 0 && <span className="muted">{data.leads.length} lead{data.leads.length === 1 ? '' : 's'}, {money(total)} charged {data.month === 'all' ? 'in all' : `in ${monthName(data.month)}`}</span>}
            <SortSelect value={sort} onChange={setSort} choices={SORTS} />
            <span style={{ marginLeft: 'auto' }}><SegmentedControl<View> segments={[{ value: 'table', label: 'List' }, { value: 'tiles', label: 'Cards' }]} value={view} onChange={setView} /></span>
          </div>
          {data.leads.length > 0 && (
            <div className="stack-tight" style={{ marginBottom: 'var(--s-6)', gap: 'var(--s-3)' }}>
              <div className="filter-chips" role="group" aria-label="Show">
                {(weeklyOrg ? WEEKLY_FILTERS : FILTERS).map((f) => <button key={f.key} type="button" className="filter-chip" aria-pressed={show === f.key} onClick={() => setShow(f.key)}>{f.label}</button>)}
              </div>
              {tiers.length > 1 && (
                <div className="filter-chips" role="group" aria-label="Tier">
                  <button type="button" className="filter-chip" aria-pressed={tier === 'all'} onClick={() => setTier('all')}>Every tier</button>
                  {tiers.map(([key, name]) => <button key={key} type="button" className="filter-chip" aria-pressed={tier === key} onClick={() => setTier(key)}>{name}</button>)}
                </div>
              )}
            </div>
          )}
          {data.leads.length === 0 ? (
            <Panel><EmptyState title={data.month === 'all' ? 'No leads yet' : `No leads in ${monthName(data.month)}`} body="Each lead shows up here the moment a rep recommends you." /></Panel>
          ) : shown.length === 0 ? (
            <Panel><EmptyState title="Nothing matches" body="Try another filter." /></Panel>
          ) : (
            view === 'table' ? <Panel className="panel-flush"><LeadTable leads={shown} onChange={reload} /></Panel> : <div className="lead-tiles">{shown.map((l) => <LeadTile key={l.id} lead={l} onChange={reload} />)}</div>
          )}
        </>
      )}
    </>
  )
}
